/**
 * NotificationToast Component
 *
 * User-friendly toast notification system for displaying errors and other messages.
 * Integrates with the PermissionErrorHandler service to show actionable error notifications.
 */

import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
// Removed permission-error-handler dependency

export interface ToastNotification {
  id: string;
  type: 'error' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  actions?: ToastAction[];
  autoClose?: boolean;
  duration?: number;
  persistent?: boolean;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

interface NotificationToastProps {
  maxNotifications?: number;
  defaultDuration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

interface ToastItemProps {
  notification: ToastNotification;
  onDismiss: (id: string) => void;
  onAction: (id: string, actionIndex: number) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ notification, onDismiss, onAction }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = useCallback(() => {
    setIsRemoving(true);
    setTimeout(() => onDismiss(notification.id), 300);
  }, [notification.id, onDismiss]);

  const handleAction = useCallback((actionIndex: number) => {
    onAction(notification.id, actionIndex);
  }, [notification.id, onAction]);

  // Auto-close functionality
  useEffect(() => {
    if (notification.autoClose && !notification.persistent) {
      const timer = setTimeout(handleDismiss, notification.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.autoClose, notification.persistent, notification.duration, handleDismiss]);

  const getTypeStyles = (type: ToastNotification['type']) => {
    const styles = {
      error: {
        container: 'bg-red-50 border-red-200 text-red-800',
        icon: 'text-red-500',
        iconPath: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
      },
      warning: {
        container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        icon: 'text-yellow-500',
        iconPath: 'M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z'
      },
      success: {
        container: 'bg-green-50 border-green-200 text-green-800',
        icon: 'text-green-500',
        iconPath: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
      },
      info: {
        container: 'bg-blue-50 border-blue-200 text-blue-800',
        icon: 'text-blue-500',
        iconPath: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
      }
    };
    return styles[type];
  };

  const typeStyle = getTypeStyles(notification.type);

  return (
    <div
      className={clsx(
        'transform transition-all duration-300 ease-in-out mb-4',
        isVisible && !isRemoving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0',
        'max-w-sm w-full'
      )}
    >
      <div className={clsx('rounded-lg border p-4 shadow-lg', typeStyle.container)}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className={clsx('w-5 h-5', typeStyle.icon)} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d={typeStyle.iconPath} clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 w-0 flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium">{notification.title}</h4>
                <p className="mt-1 text-sm opacity-90">{notification.message}</p>

                {/* Error Details - removed */}

                {/* Action Buttons */}
                {notification.actions && notification.actions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {notification.actions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleAction(index)}
                        className={clsx(
                          'text-xs px-3 py-1 rounded-md font-medium transition-colors',
                          action.variant === 'primary' && 'bg-blue-600 hover:bg-blue-700 text-white',
                          action.variant === 'danger' && 'bg-red-600 hover:bg-red-700 text-white',
                          (!action.variant || action.variant === 'secondary') && 'bg-gray-600 hover:bg-gray-700 text-white'
                        )}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={handleDismiss}
                  className={clsx(
                    'inline-flex rounded-md p-1.5 transition-colors',
                    typeStyle.icon,
                    'hover:bg-black hover:bg-opacity-10'
                  )}
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationToast: React.FC<NotificationToastProps> = ({
  position = 'top-right'
}) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  // Permission error handler subscription removed

  // addNotification function removed - not used anymore

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const handleAction = useCallback((notificationId: string, actionIndex: number) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification?.actions?.[actionIndex]) {
      const action = notification.actions[actionIndex];
      action.onClick();

      // Error resolution handling removed

      // Remove notification after action
      removeNotification(notificationId);
    }
  }, [notifications, removeNotification]);

  const getPositionStyles = () => {
    const positions = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
      'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
    };
    return positions[position];
  };

  // getErrorTitle function removed - not used anymore

  // createActionsFromSuggestions function removed

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={clsx('fixed z-notification-toast', getPositionStyles())}>
      <div className="flex flex-col">
        {notifications.map(notification => (
          <ToastItem
            key={notification.id}
            notification={notification}
            onDismiss={removeNotification}
            onAction={handleAction}
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationToast;